/** Whether a panel can consume a vertical gesture before the page advances. */
export function canScrollPanel(top: number, height: number, viewport: number, delta: number): boolean {
  return delta < 0 ? top > 1 : delta > 0 && top + viewport < height - 1;
}

/** Sticky overflow:hidden ancestors can swallow native scroll chaining. */
export function chainBeatScroll(el: HTMLElement): () => void {
  const canConsume = (target: EventTarget | null, delta: number): boolean => {
    let node = target instanceof HTMLElement ? target : el;
    while (el.contains(node)) {
      if (canScrollPanel(node.scrollTop, node.scrollHeight, node.clientHeight, delta)
        && /auto|scroll/.test(getComputedStyle(node).overflowY)) return true;
      if (node === el || !node.parentElement) break;
      node = node.parentElement;
    }
    return false;
  };
  const forward = (event: Event, delta: number): void => {
    if (!delta || canConsume(event.target, delta)) return;
    event.preventDefault();
    window.scrollBy({ top: delta, behavior: 'instant' });
  };
  const wheel = (event: WheelEvent): void => {
    if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? el.clientHeight : 1;
    forward(event, event.deltaY * unit);
  };
  let touch: { x: number; y: number } | null = null;
  const start = (event: TouchEvent): void => {
    const t = event.touches.length === 1 ? event.touches[0] : undefined;
    touch = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const move = (event: TouchEvent): void => {
    const t = event.touches.length === 1 ? event.touches[0] : undefined;
    if (!t || !touch) return;
    const delta = touch.y - t.clientY;
    const horizontal = Math.abs(touch.x - t.clientX) > Math.abs(delta);
    touch = { x: t.clientX, y: t.clientY };
    if (!horizontal) forward(event, delta);
  };
  el.addEventListener('wheel', wheel, { passive: false });
  el.addEventListener('touchstart', start, { passive: true });
  el.addEventListener('touchmove', move, { passive: false });
  return () => {
    el.removeEventListener('wheel', wheel);
    el.removeEventListener('touchstart', start);
    el.removeEventListener('touchmove', move);
  };
}
